/**
 * Admin transcription workflows page for /admin/workflows route.
 * Read-only list of the workflows an admin can pick per workspace.
 */

import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { useWorkflows } from "../hooks/useAdminWorkflows";
import type { WorkflowEntry } from "../services/naraDoor";
import { Mic } from "lucide-react";
import { KiiList, type KiiListColumn } from "../components/kii";
import { KiiBadge } from "../components/kii/kii-badge";
import { TableRow, TableCell } from "../components/ui/table";
import { publicRoutes } from "../lib/routes";

export function AdminWorkflows() {
  const { t } = useTranslation();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const { data, isLoading, error } = useWorkflows();

  const columns: KiiListColumn[] = [
    { key: "name", label: t("adminWorkflows.columns.name") },
    { key: "speechModel", label: t("adminWorkflows.columns.speechModel") },
    { key: "status", label: t("adminWorkflows.columns.status") },
    { key: "description", label: t("adminWorkflows.columns.description") },
  ];

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  return (
    <div>
      <PageHeader
        title={t("adminWorkflows.title")}
        description={t("adminWorkflows.description")}
        isAdmin
        breadcrumbs={[
          { label: t("adminWorkflows.breadcrumb.dashboard"), href: publicRoutes.home },
          { label: t("adminWorkflows.breadcrumb.admin") },
          { label: t("adminWorkflows.breadcrumb.workflows") },
        ]}
      />

      <KiiList<WorkflowEntry>
        columns={columns}
        data={data?.workflows ?? []}
        isLoading={isLoading}
        emptyMessage={error ? t("adminWorkflows.error.load") : t("adminWorkflows.empty")}
        emptyIcon={<Mic className="h-10 w-10 text-muted-foreground" />}
        renderRow={(workflow) => (
          <TableRow key={workflow.id}>
            <TableCell className="font-medium">{workflow.label}</TableCell>
            <TableCell className="font-mono text-xs">{workflow.stt_model}</TableCell>
            <TableCell>
              <KiiBadge variant={workflow.status === "ga" ? "success" : "secondary"}>
                {t(`adminWorkflows.status.${workflow.status}`)}
              </KiiBadge>
            </TableCell>
            <TableCell className="text-muted-foreground">{workflow.description}</TableCell>
          </TableRow>
        )}
      />
    </div>
  );
}
